import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Mail } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useQuery } from "@tanstack/react-query";
import { Alert, AlertDescription } from "@/components/ui/alert";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [emailSent, setEmailSent] = useState(false);
  
  const navigate = useNavigate();

  // Check if user is already logged in
  const { data: session, isLoading: isSessionLoading } = useQuery({
    queryKey: ['session'],
    queryFn: async () => {
      const { data: { session }, error } = await supabase.auth.getSession();
      if (error) throw error;
      return session;
    },
  });

  // Redirect if already logged in
  useEffect(() => {
    if (session) {
      navigate("/getting-started", { replace: true });
    }
  }, [session, navigate]);

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setAuthError(null);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) {
        console.error("Password reset error:", error);
        setAuthError(error.message);
        toast.error(error.message);
        return;
      }

      setEmailSent(true);
      toast.success("Password reset email sent! Please check your inbox.");
    } catch (error) {
      console.error("Unexpected error:", error);
      toast.error("An unexpected error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  // Show loading state while checking session
  if (isSessionLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="w-full max-w-7xl px-12 py-8 flex flex-col md:flex-row items-center justify-center gap-20">
        {/* Logo section */}
        <div className="w-full md:w-1/2 flex justify-center">
          <img 
            src="/lovable-uploads/496f0ef7-a54a-4a74-a39b-0a3c3067bb55.png" 
            alt="Smplify Logo" 
            className="max-w-[250px] md:max-w-[300px] w-full object-contain"
          />
        </div>

        {/* Form section */}
        <div className="w-full md:w-1/2 flex justify-center">
          <div className="w-full max-w-md space-y-6 bg-card rounded-lg shadow-lg border p-8">
            {authError && (
              <Alert variant="destructive" className="mb-4">
                <AlertDescription>{authError}</AlertDescription>
              </Alert>
            )}
            
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-foreground">Reset your password</h2>
              <p className="mt-2 text-sm text-muted-foreground">
                Enter your email address and we'll send you a link to reset your password
              </p>
            </div>

            {!emailSent ? (
              <form onSubmit={handlePasswordReset} className="space-y-4">
                <div className="space-y-2">
                  <Input
                    type="email"
                    placeholder="Email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="w-full"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    "Sending reset email..."
                  ) : (
                    <>
                      <Mail className="w-4 h-4 mr-2" />
                      Send reset email
                    </>
                  )}
                </Button>
              </form>
            ) : (
              <div className="text-center space-y-4">
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <Mail className="w-8 h-8 text-green-600 mx-auto mb-2" />
                  <p className="text-sm text-green-800">
                    Password reset email sent to <strong>{email}</strong>
                  </p>
                  <p className="text-xs text-green-600 mt-1">
                    Please check your inbox and follow the instructions to reset your password.
                  </p>
                </div>
              </div>
            )}

            <div className="text-center text-sm text-muted-foreground">
              <p>
                Remember your password?{" "}
                <Link to="/login" className="text-primary hover:underline">
                  Sign in
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}